/**
 * A refusal from any service, thrown by every client call that does not
 * answer 2xx (ADR-0065).
 *
 * Branch on `type` where the service names one, and on `status` where it
 * still answers `about:blank`; show `detail`, never parse it. `instance` is
 * the request id, which is what turns a screenshot into a log query.
 */
export class ApiProblem extends Error {
    type;
    title;
    status;
    detail;
    instance;
    constructor(problem) {
        super(problem.detail ?? problem.title);
        this.name = 'ApiProblem';
        this.type = problem.type;
        this.title = problem.title;
        this.status = problem.status;
        if (problem.detail !== undefined)
            this.detail = problem.detail;
        if (problem.instance !== undefined)
            this.instance = problem.instance;
    }
    /**
     * Reads a refused response. A body that is not problem+json — a proxy's
     * HTML 502, an empty 401, a JSON `null` — becomes `about:blank` titled by
     * its status text, or `HTTP <status>` where there is none (HTTP/2). A field
     * of the wrong type is replaced, so every refusal reaches the app in the
     * same shape.
     */
    static async from(response) {
        const fallback = {
            type: 'about:blank',
            title: response.statusText || `HTTP ${response.status}`,
            status: response.status,
        };
        if (!response.headers.get('content-type')?.includes('json'))
            return new ApiProblem(fallback);
        const body = await response.json().catch(() => undefined);
        if (typeof body !== 'object' || body === null)
            return new ApiProblem(fallback);
        const { type, title, status, detail, instance } = body;
        return new ApiProblem({
            type: typeof type === 'string' ? type : fallback.type,
            title: typeof title === 'string' && title ? title : fallback.title,
            status: typeof status === 'number' ? status : fallback.status,
            ...(typeof detail === 'string' ? { detail } : {}),
            ...(typeof instance === 'string' ? { instance } : {}),
        });
    }
}
//# sourceMappingURL=problem.js.map